// src/pages/Roles/RoleList.jsx
// -----------------------------------------------------------------------------
// Admin screen listing and managing base roles using reusable components.
// Performs full CRUD (Read, Create, Update, Delete) locally or via roleService.
// -----------------------------------------------------------------------------
import roleService from '../../api/roleService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import { useNavigate } from 'react-router-dom';

// Schema configuration for Role_TBL
const ROLE_FIELDS = [
  {
    name: 'roleID',
    label: 'ID',
    type: 'integer',
    isPrimaryKey: true,
    readOnly: true,
  },
  {
    name: 'roleName',
    label: 'Role Name',
    type: 'varchar',
    required: true,
    maxLength: 50,
    placeholder: 'e.g. QA Engineer, Lead Developer',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    maxLength: 255,
    placeholder: 'What permissions or access levels this role has...',
  },
];

export default function RoleList() {
  const navigate = useNavigate();
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(roleService, {
    idKey: 'roleID',
    itemName: 'Role',
    initialSort: { key: 'roleID', direction: 'asc' },
  });

  const columns = [
    { key: 'roleID', label: 'ID', sortable: true, width: '80px' },
    { key: 'roleName', label: 'Role Name', sortable: true },
    { key: 'description', label: 'Description', sortable: true },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Roles</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Configure and manage the base access levels available across the application.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Role
        </button>
      </div>

      {/* Reusable Table List */}
      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="roleID"
        emptyMessage="No roles found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        renderActions={(row) => (
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginRight: '8px', padding: '5px 10px', fontSize: '12px' }}
            onClick={() => navigate(`/permissions?roleID=${row.roleID}`)}
            title="Manage Permissions"
          >
            🔑 Permissions
          </button>
        )}
      />

      {/* Dynamic Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={ROLE_FIELDS}
          initialValues={selectedRecord || { roleName: '', description: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Role"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Role"
        itemLabel={selectedRecord ? selectedRecord.roleName : 'Role'}
        deleting={deleting}
      />
    </div>
  );
}